import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@/shared/ui/theme/ThemeProvider';
import { Avatar } from '@/presentation/components/Avatar';
import { CountdownBar } from '@/presentation/components/CountdownBar';
import { useClock } from '@/shared/hooks/useClock';
import { formatTimeWithSeconds } from '@/shared/utils/datetime';
import { KIOSK_AUTO_RESET_SECONDS } from '@/shared/config/terminal.config';
import type { AttendanceAction } from '../../domain/entities/Timelog';
import type { AttendanceEmployee } from '../../infrastructure/mappers/timelogMapper';

export function MarkingSuccessPage() {
  const { t } = useTranslation('common');
  const { C, T } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as { action?: AttendanceAction; employee?: AttendanceEmployee } | null;
  const action = state?.action ?? 'entrada';
  const employee = state?.employee ?? null;
  const now = useClock();

  useEffect(() => {
    const timer = setTimeout(() => { void navigate('/', { replace: true }); }, KIOSK_AUTO_RESET_SECONDS * 1000);
    return () => { clearTimeout(timer); };
  }, [navigate]);

  return (
    <main style={{ flex: 1, background: T.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '30px' }}>
      <div className="mi-anim-slide-up" style={{ flex: 1, width: '100%', maxWidth: 407, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        {/* Check icon */}
        <div className="mi-anim-check-pop" style={{ marginBottom: 23 }}>
          <div style={{ width: 99, height: 99, borderRadius: '50%', background: `${C.success}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ width: 75, height: 75, borderRadius: '50%', background: C.success, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width={35} height={35} viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
          </div>
        </div>

        <div style={{ fontSize: 23, fontWeight: 700, color: T.clockText, marginBottom: 6 }}>{t('success.title')}</div>
        <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 26 }}>{formatTimeWithSeconds(now)}</div>

        {/* Employee card */}
        {employee && (
          <div style={{ width: '100%', background: T.surfaceBg, borderRadius: 13, padding: '17px 23px', border: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', gap: 15, marginBottom: 26, boxShadow: T.shadow }}>
            <Avatar initials={employee.initials} color={employee.avatarColor} size={46} photoUrl={employee.photoUrl ?? null} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{employee.fullName}</div>
              <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>{employee.code} · {employee.department}</div>
            </div>
            <div style={{ padding: '5px 13px', borderRadius: 16, fontSize: 10, fontWeight: 700, background: action === 'automatic' ? `${C.blue}18` : action === 'entrada' ? `${C.success}18` : `${C.danger}18`, color: action === 'automatic' ? C.blue : action === 'entrada' ? C.success : C.danger }}>
              {action === 'automatic' ? t('success.labelAutomatic') : action === 'entrada' ? t('success.labelEntry') : t('success.labelExit')}
            </div>
          </div>
        )}

        {/* Countdown */}
        <div style={{ width: '100%', marginBottom: 23 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ fontSize: 10, color: T.textMuted, fontWeight: 500 }}>{t('success.countdown')}</span>
            <span style={{ fontSize: 10, color: T.textMuted }}>{KIOSK_AUTO_RESET_SECONDS}s</span>
          </div>
          <CountdownBar seconds={KIOSK_AUTO_RESET_SECONDS} color={C.success} T={T} />
        </div>

        <button
          onClick={() => { void navigate('/', { replace: true }); }}
          style={{ padding: '10px 32px', borderRadius: 9, border: `1px solid ${T.border}`, background: 'transparent', fontFamily: 'inherit', fontSize: 11, fontWeight: 600, color: T.text, cursor: 'pointer' }}
        >
          {t('success.returnBtn')}
        </button>
      </div>
    </main>
  );
}
